// ========================================
// UTILIDAD DE SUBIDA DIRECTA A S3 - DOCUMENTOS
// Sistema INDEPENDIENTE para gestión documental
// Límite: 100MB (vs 11MB de surveys)
// ========================================

/**
 * Sube documento directamente a S3 usando presigned URLs
 * @param {File} file - Archivo a subir (ya comprimido si aplica)
 * @param {Object} params - Parámetros del documento
 * @param {number} params.documentId - ID del documento
 * @returns {Promise<Object>} Resultado de la subida
 */
export async function uploadDocumentDirectlyToS3(file, params) {
    const { documentId } = params;

    try {
        console.log('🚀 [DOCUMENTOS] Iniciando subida directa a S3...');
        console.log(`📁 Archivo: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);

        // PASO 1: Solicitar URL firmada al backend
        console.log('📝 Solicitando URL firmada...');

        // Construir body - solo incluir document_id si existe (para updates)
        const requestBody = {
            file_name: file.name,
            content_type: file.type,
            file_size: file.size
        };

        if (documentId) {
            requestBody.document_id = documentId;
            console.log(`📝 Actualizando documento existente: ${documentId}`);
        } else {
            console.log('📝 Nuevo documento - subiendo a carpeta temporal');
        }

        const urlResponse = await fetch('/document_management/generate_document_upload_url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify(requestBody)
        });

        if (!urlResponse.ok) {
            const errorData = await urlResponse.json();
            throw new Error(errorData.error || 'Error obteniendo URL firmada');
        }

        const { upload_url, file_key, document_id, expires_in } = await urlResponse.json();
        console.log('✅ URL firmada obtenida');
        console.log(`⏱️  Expira en: ${expires_in} segundos`);

        // PASO 2: Subir archivo directamente a S3
        console.log('⬆️  Subiendo archivo a S3...');
        const uploadResponse = await fetch(upload_url, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
                'Content-Length': file.size.toString()
            }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Error subiendo a S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        console.log('✅ Archivo subido a S3 exitosamente');

        // PASO 3: Confirmar subida en el backend
        console.log('✅ Confirmando subida en backend...');

        // Construir body - solo incluir document_id si existe
        const confirmBody = {
            file_key: file_key
        };

        if (document_id) {
            confirmBody.document_id = document_id;
        }

        const confirmResponse = await fetch('/document_management/confirm_document_upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify(confirmBody)
        });

        if (!confirmResponse.ok) {
            const errorData = await confirmResponse.json();
            throw new Error(errorData.error || 'Error confirmando subida');
        }

        const confirmData = await confirmResponse.json();
        console.log('🎉 Subida completada exitosamente');

        return {
            success: true,
            file_url: confirmData.file_url,
            document_id: confirmData.document_id,
            file_name: file.name,
            file_size: file.size,
            message: 'Archivo subido exitosamente'
        };

    } catch (error) {
        console.error('❌ Error en subida directa S3 (documentos):', error);

        // Retornar error para que el llamador decida el fallback
        return {
            success: false,
            error: error.message,
            shouldFallback: true
        };
    }
}

/**
 * Upload con progreso y retry automático
 * @param {File} file - Archivo a subir
 * @param {Object} params - Parámetros
 * @param {Function} onProgress - Callback de progreso (opcional)
 * @returns {Promise<Object>} Resultado
 */
export async function uploadDocumentWithProgress(file, params, onProgress = null) {
    const maxRetries = 3;
    let attempt = 0;

    while (attempt < maxRetries) {
        try {
            if (onProgress) {
                onProgress({
                    phase: 'preparing',
                    progress: 0,
                    message: 'Preparando subida...'
                });
            }

            const result = await uploadDocumentDirectlyToS3(file, params);

            if (result.success) {
                if (onProgress) {
                    onProgress({
                        phase: 'completed',
                        progress: 100,
                        message: '¡Archivo subido exitosamente!'
                    });
                }
                return result;
            } else {
                throw new Error(result.error);
            }

        } catch (error) {
            attempt++;
            console.warn(`⚠️  Intento ${attempt} de ${maxRetries} falló:`, error.message);

            if (attempt >= maxRetries) {
                if (onProgress) {
                    onProgress({
                        phase: 'failed',
                        progress: 0,
                        message: 'Error subiendo archivo'
                    });
                }

                return {
                    success: false,
                    error: error.message,
                    shouldFallback: true
                };
            }

            // Esperar antes del siguiente intento (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));

            if (onProgress) {
                onProgress({
                    phase: 'retrying',
                    progress: 0,
                    message: `Reintentando subida... (${attempt}/${maxRetries})`
                });
            }
        }
    }
}

/**
 * Valida si el archivo puede subirse directamente a S3
 * @param {File} file - Archivo a validar
 * @returns {Object} Resultado de validación
 */
export function validateDocumentForS3Upload(file) {
    // Validar tamaño (100MB máximo para documentos)
    const MAX_SIZE = 104857600; // 100MB en bytes

    if (file.size > MAX_SIZE) {
        return {
            isValid: false,
            error: `El archivo excede el límite de 100 MB permitido (tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
        };
    }

    // Validar que el archivo existe
    if (!file || !file.name) {
        return {
            isValid: false,
            error: 'No se ha seleccionado ningún archivo'
        };
    }

    // Tipos de archivo comunes para documentos
    const commonDocumentTypes = [
        // Documentos
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Imágenes
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'image/bmp',
        // Texto
        'text/plain',
        'text/csv',
        'text/html',
        // Comprimidos
        'application/zip',
        'application/x-rar-compressed',
        'application/x-7z-compressed'
    ];

    // Por ahora, permitir todos los tipos (backend maneja validación)
    // Solo advertir si es un tipo no común
    const warning = commonDocumentTypes.includes(file.type) ? null :
                   'Tipo de archivo no común, pero será procesado';

    return {
        isValid: true,
        warning: warning
    };
}

/**
 * Elimina un archivo de S3
 * @param {string} fileUrl - URL del archivo en S3
 * @returns {Promise<Object>} Resultado
 */
export async function deleteDocumentFromS3(fileUrl) {
    try {
        console.log('🗑️  Eliminando archivo de S3...');
        console.log(`URL: ${fileUrl}`);

        const response = await fetch('/document_management/delete_document_s3_file', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            },
            body: JSON.stringify({
                file_url: fileUrl
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Error eliminando archivo');
        }

        const data = await response.json();
        console.log('✅ Archivo eliminado exitosamente');

        return {
            success: true,
            message: data.message
        };

    } catch (error) {
        console.error('❌ Error eliminando archivo de S3:', error);
        return {
            success: false,
            error: error.message
        };
    }
}

/**
 * Función principal que combina compresión + subida directa
 * Para documentos, la compresión solo se aplica a imágenes
 * @param {File} originalFile - Archivo original del usuario
 * @param {Object} params - Parámetros del documento
 * @param {Function} onProgress - Callback opcional de progreso
 * @returns {Promise<Object>} Resultado completo
 */
export async function compressAndUploadDocument(originalFile, params, onProgress = null) {
    try {
        let fileToUpload = originalFile;
        let compressionData = null;

        // PASO 1: Comprimir solo si es imagen
        const isImage = originalFile.type.startsWith('image/');

        if (isImage) {
            if (onProgress) {
                onProgress({
                    phase: 'compressing',
                    progress: 25,
                    message: '🗜️ Optimizando imagen...'
                });
            }

            try {
                // Importar utilidad de compresión
                const { compressFileForBeewo } = await import('./imageCompression.js');

                console.log('🗜️ Comprimiendo imagen...');
                const compressedFile = await compressFileForBeewo(originalFile);

                const originalSizeMB = (originalFile.size / 1024 / 1024).toFixed(2);
                const compressedSizeMB = (compressedFile.size / 1024 / 1024).toFixed(2);
                const reduction = originalFile.size !== compressedFile.size ?
                    (((originalFile.size - compressedFile.size) / originalFile.size) * 100).toFixed(1) : 0;

                console.log(`📊 Compresión: ${originalSizeMB}MB → ${compressedSizeMB}MB (${reduction}% reducción)`);

                fileToUpload = compressedFile;
                compressionData = {
                    originalSize: originalFile.size,
                    compressedSize: compressedFile.size,
                    reduction: reduction + '%'
                };
            } catch (compressionError) {
                console.warn('⚠️  Error en compresión, usando archivo original:', compressionError);
                // Continuar con archivo original
            }
        } else {
            console.log('📄 Archivo no es imagen, subiendo sin compresión');
        }

        // Validar tamaño DESPUÉS de comprimir (si aplica)
        const validation = validateDocumentForS3Upload(fileToUpload);
        if (!validation.isValid) {
            return {
                success: false,
                error: validation.error,
                shouldFallback: false
            };
        }

        // PASO 2: Subir directamente a S3
        if (onProgress) {
            onProgress({
                phase: 'uploading',
                progress: 50,
                message: '☁️  Subiendo a la nube...'
            });
        }

        const uploadResult = await uploadDocumentWithProgress(
            fileToUpload,
            params,
            (uploadProgress) => {
                if (onProgress) {
                    onProgress({
                        phase: 'uploading',
                        progress: 50 + (uploadProgress.progress * 0.5), // 50-100%
                        message: uploadProgress.message
                    });
                }
            }
        );

        return {
            ...uploadResult,
            compressionData: compressionData
        };

    } catch (error) {
        console.error('❌ Error en proceso completo (documentos):', error);
        return {
            success: false,
            error: error.message,
            shouldFallback: true
        };
    }
}

// Exportar funciones para uso directo
export default {
    uploadDocumentDirectlyToS3,
    uploadDocumentWithProgress,
    validateDocumentForS3Upload,
    deleteDocumentFromS3,
    compressAndUploadDocument
};
